import { type ZodObject, type ZodType, z } from 'zod'

export const apiSuccessSchema = <T>(itemSchema: ZodType<T>) =>
  z.object({
    items: z.array(itemSchema),
    limit: z.number().optional(),
    next: z.string().optional()
  })

/**
 * Abstract class for creating a response schema for an api endpoint
 */
abstract class _ApiSchema<T extends ZodType, O extends ZodObject> {
  public responseSchema

  protected constructor(
    public itemSchema: T,
    responseOptionalSchema: O
  ) {
    this.responseSchema = z.object({ items: z.array(itemSchema) }).merge(responseOptionalSchema)
  }

  parseResponse = (response: unknown) => {
    return this.responseSchema.parse(response)
  }

  parseItem = (item: unknown): z.infer<T> => {
    return this.itemSchema.parse(item)
  }
}

/**
 * Type of the item returned by parseItem
 */
export type ItemReturnType<T extends _ApiSchema<ZodType, ZodObject>> = ReturnType<T['parseItem']>

/**
 * Type of the response returned by parseResponse
 */
export type ResponseReturnType<T extends _ApiSchema<ZodType, ZodObject>> = ReturnType<
  T['parseResponse']
>

const noOptionals = z.object({})

/**
 * Helper class for creating a response schema for an api endpoint
 *
 * If needed, clients should create their own type aliases for the item and
 * response:
 *
 * `type Item = ItemReturnType<typeof MyApi>`<p>
 * `type Response = ResponseReturnType<typeof MyApi>`
 */
export class ApiSchema<I extends ZodType> extends _ApiSchema<I, typeof noOptionals> {
  constructor(itemSchema: I) {
    super(itemSchema, noOptionals)
  }
}

const paginatedOptionals = z.object({
  limit: z.number().optional(),
  next: z.number().optional()
})

/**
 * Helper class for creating a paginated response schema for an api endpoint
 *
 * If needed, clients should create their own type aliases for the item and
 * response:
 *
 * `type Item = ItemReturnType<typeof MyApi>`<p>
 * `type Response = ResponseReturnType<typeof MyApi>`
 */
export class ApiPaginatedSchema<I extends ZodType> extends _ApiSchema<
  I,
  typeof paginatedOptionals
> {
  constructor(itemSchema: I) {
    super(itemSchema, paginatedOptionals)
  }
}
